/**
 * What the ⌘K command bar can open, and how a query ranks it
 * (platform-restructure-spec v2 §5.5; v3 navigation).
 *
 * The index is what the new navigation can reach: the sidebar's rows and every tab
 * (pillarNavConfig.js, pillarTabs.js — same gates), the filtered views those pages
 * read from their URL, and every product module's pages (modulePages.js), which the
 * new navigation no longer draws in the sidebar. Unlicensed pages are left out.
 */
import { PILLARS, PILLAR_BOTTOM, PILLAR_TOP, passesGates } from '../Sidebar/pillarNavConfig';
import { TAB_GROUPS, tabShown } from '../Sidebar/pillarTabs';
import { modulePages, moduleSpec } from '../Sidebar/modulePages';
import { NAV_SECTIONS } from '../../../config/nav/catalog';

// Words people type for a page that its label does not contain (spec §5.5's
// required resolutions, as far as pages go). Matched in English only.
export const ALIASES = Object.freeze({
  '/agent-atlas/assessment/red-team': ['red teaming', 'pen test', 'attack simulation'],
  '/agent-atlas/assessment/evaluations': ['model evals', 'evals', 'benchmark'],
  '/agent-atlas/assessment': ['risks'],
  '/agent-atlas/finops': ['spend', 'cost', 'budget', 'tokens', 'roi'],
  '/security-atlas/deepfake-detector': ['deepfake', 'synthetic media'],
  '/agent-atlas/policy': ['guardrails', 'rules'],
  '/admin/custom-guardrails': ['custom guardrails', 'default guardrails', 'keyword rules', 'regex rules'],
  '/agent-atlas/inventory': ['inventory', 'agents', 'assets'],
  '/security-atlas/ai-bom': ['mcp', 'aibom', 'models', 'tools'],
  '/security-atlas/mcp-security': ['mcp', 'tool calls'],
  '/security-atlas/ai-bom?view=mcp-skills-tools': ['mcp', 'mcp servers', 'skills', 'plugins', 'tools'],
  '/security-atlas/signals': ['signals'],
  '/security-atlas/data-security': ['dlp', 'pii', 'data exposure'],
  '/security-atlas/overview': ['soc', 'overview'],
  '/agent-atlas/log-explorer': ['logs', 'events'],
  '/agent-atlas/alerts': ['notifications', 'alerts'],
  '/agent-atlas/adoption/knowledge-gaps': ['knowledge gaps'],
  '/agent-atlas/adoption/intent-analysis': ['intents', 'intent analysis'],
  '/security-atlas/tuning': ['mutes', 'suppressions'],
  // The Playbooks page retired into Skills & Workflows' Workflows tab.
  '/security-atlas/skills': ['playbooks', 'workflows', 'soar'],
  '/endpoint-security': ['endpoint'],
  '/endpoint-security/tool-catalog': ['shadow ai', 'ai apps'],
  '/endpoint-security/endpoints': ['devices', 'laptops'],
  '/admin/audit-log': ['who changed what'],
});

// The AI-BOM filtered to a kind of asset (/security-atlas/ai-bom?type=…, Discovery › AI Bill of
// Materials): [graph type, label key, words people use for it].
const AI_BOM_KINDS = Object.freeze([
  ['Model', 'models', ['llms', 'foundation models']],
  ['Tool', 'tools', ['ai tools']],
  ['MCPServer', 'mcpServers', ['mcp']],
  ['Datastore', 'dataSources', ['knowledge', 'datasets', 'datastores']],
  ['Application', 'applications', ['apps']],
  ['Account', 'identities', ['accounts', 'service accounts']],
  ['SubAgent', 'subAgents', ['subagents']],
]);

// Detections' type views (/security-atlas/signals?type=…, the Signals page under
// "New navigation"): [type, label key, words people use for it].
const DETECTION_TYPES = Object.freeze([
  ['injection', 'typeInjection', ['prompt injection', 'jailbreak']],
  ['data_exposure', 'typeDataExposure', ['pii', 'dlp', 'sensitive data', 'data leak']],
  ['harmful_content', 'typeHarmfulContent', ['toxic', 'hate', 'weapons']],
  ['agent_behaviour', 'typeAgentBehaviour', ['tool calls', 'agent actions']],
  ['posture', 'typePosture', ['misconfiguration', 'access', 'shadow access']],
  ['instructions', 'typeInstructions', ['agent instructions', 'instructions', 'skills', 'risky instructions']],
  ['cloud', 'typeCloud', ['cloud audit']],
  ['code', 'typeCode', ['sast', 'code findings']],
  ['policy', 'typePolicy', ['policy violations', 'violations']],
  ['deepfake', 'typeDeepfake', ['deepfake', 'synthetic media']],
]);

// Where a tab group's tabs are found: the sidebar row that opens the group, or the account
// menu's Settings / Profile.
const groupParents = (t) => {
  const parents = { settings: t('nav:pillarTabs.groups.settings'), profile: t('nav:pillarTabs.groups.profile') };
  [...PILLAR_TOP, ...PILLAR_BOTTOM].forEach((it) => { if (it.group) parents[it.group] = t(`nav:${it.labelKey}`); });
  PILLARS.forEach((p) => p.items.forEach((it) => {
    if (it.group) parents[it.group] = `${t(`nav:${p.labelKey}`)} › ${t(`nav:${it.labelKey}`)}`;
  }));
  return parents;
};

/**
 * Every entry the user may open: { to, label, where, keywords }.
 *   t          i18next t with the nav, layout, agentAtlas and securityAtlas namespaces
 *   gates      { canGovern, orgId, featureOn } as the pillar sidebar uses
 *   isAdmin    an administrator (Settings, admin-only tabs)
 *   canBill    may open Billing
 *   moduleCtx  the classic module rows' gates (modulePages.js ctx)
 *   isLocked   (path) => true when the org does not hold the page's module
 */
export const buildCommandIndex = ({ t, gates, isAdmin = false, canBill = false, moduleCtx = null, isLocked = () => false }) => {
  const entries = [];
  const add = (to, label, where, keywords = ALIASES[to] || []) => {
    if (!to || isLocked(to.split('?')[0]) || entries.some((e) => e.to === to)) return;
    entries.push({ to, label, where, keywords });
  };

  [...PILLAR_TOP, ...PILLAR_BOTTOM].filter((it) => passesGates(it, gates)).forEach((it) => add(it.to, t(`nav:${it.labelKey}`), ''));
  PILLARS.forEach((p) => {
    const pillar = t(`nav:${p.labelKey}`);
    p.items.filter((it) => it.to && passesGates(it, gates)).forEach((it) => add(it.to, t(`nav:${it.labelKey}`), pillar));
  });

  const tabCtx = { isAdmin, canBill, orgId: gates.orgId, featureOn: gates.featureOn };
  const parents = groupParents(t);
  Object.entries(TAB_GROUPS).forEach(([group, tabs]) => {
    tabs.filter((tab) => tabShown(tab, tabCtx)).forEach((tab) => add(tab.to, t(tab.labelKey), parents[group]));
  });

  const detections = `${t('nav:pillars.security')} › ${t('nav:pillarItems.detections')}`;
  DETECTION_TYPES.forEach(([type, labelKey, words]) => add(
    `/security-atlas/signals?type=${type}`,
    `${t('nav:pillarItems.detections')}: ${t(`securityAtlas:signals.${labelKey}`)}`,
    detections,
    words,
  ));

  const aiBom = parents.aiBom;
  AI_BOM_KINDS.forEach(([type, labelKey, words]) => add(
    `/security-atlas/ai-bom?type=${type}`, t(`nav:pillarTabs.kinds.${labelKey}`), aiBom, words,
  ));

  if (moduleCtx) {
    Object.entries(NAV_SECTIONS).filter(([key, sec]) => sec.surface && key !== 'agent-atlas').forEach(([key, sec]) => {
      const spec = moduleSpec(key);
      if (!spec) return;
      const name = t(sec.labelKey);
      modulePages(spec, moduleCtx).forEach((page) => add(page.to, page.label || t(page.labelKey), name));
    });
  }
  return entries;
};

const norm = (s) => String(s || '').toLowerCase().trim();

/**
 * Entries matching `query`, best first: label starts with it, then a keyword
 * starts with it, then label / keyword / location contains it. Ties keep
 * sidebar order. An empty query lists the first `limit` entries.
 */
export const searchCommands = (entries, query, limit = 8) => {
  const q = norm(query);
  if (!q) return entries.slice(0, limit);
  const score = (e) => {
    const label = norm(e.label);
    const words = e.keywords.map(norm);
    if (label.startsWith(q)) return 5;
    if (label.split(/[\s&›/-]+/).some((w) => w.startsWith(q))) return 4;
    if (words.some((w) => w.startsWith(q))) return 3;
    if (label.includes(q) || words.some((w) => w.includes(q))) return 2;
    if (norm(e.where).includes(q)) return 1;
    return 0;
  };
  return entries
    .map((e, i) => ({ e, i, s: score(e) }))
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s || a.i - b.i)
    .slice(0, limit)
    .map((x) => x.e);
};
